import moment from 'moment'

import {FieldsForm} from '../../fields/types'
import {phoneNumberToString} from '../../utils/phoneNumberUtils'
import {formatTimeToHHMMSS} from '../../utils/timeUtils'

import {OrderRequestPayload} from './types'

/**
 * Options for mapping order form to order request payload
 */
export interface MapOrderFormToOrderRequestOptions {
  /** Job site information */
  site: {
    country: string
    orgUnitId: string
    customerId: string
    siteId: string
  }
  /** Optional copied order ID for repeat orders */
  copiedFromOrderId?: string

  /** Optional index of the order to create, by default index is equal to 0 */
  orderIndex?: number
}

/**
 * Formats shipping date as ISO datetime string
 * Uses user-selected date/time from form, falls back to current time
 */
const formatShippingDate = (formData: FieldsForm, index: number): string => {
  if (formData.dateTime && formData.dateTime[index]) {
    return moment(formData.dateTime[index]).toISOString()
  }
  return moment().toISOString()
}

/**
 * Builds pump configuration from form data
 * Returns empty object if no pump is configured
 */
const buildPumpConfiguration = (
  formData: FieldsForm
): Pick<OrderRequestPayload, 'pumpConfiguration'> => {
  if (!formData.unloadingMethod?.pump) {
    return {}
  }

  const config = formData.unloadingMethod.pump
  const {
    discharge: {method: discharge, ...dischargeOptions},
    ...rest
  } = config.pumpSettings

  return {
    pumpConfiguration: {
      pumpNumber: config.pumpNumber,
      discharge,
      ...dischargeOptions,
      ...rest
    }
  }
}

/**
 * Gets time on job site if configured
 */
const getTimeOnJobSite = (formData: FieldsForm): Pick<OrderRequestPayload, 'timeOnJobSite'> => {
  if (formData.timeOnJobSite) {
    return {timeOnJobSite: formatTimeToHHMMSS(formData.timeOnJobSite.time)}
  }
  return {}
}

/**
 * Gets spacing or flow rate based on truck scenario
 */
const getSpacingOrFlowRate = (
  formData: FieldsForm
): Pick<OrderRequestPayload, 'loadSpace' | 'flowRate'> => {
  if (!formData.spacingOrFlowRate) {
    return {}
  }

  if ('loadSpace' in formData.spacingOrFlowRate) {
    return {loadSpace: formatTimeToHHMMSS(formData.spacingOrFlowRate.loadSpace)}
  }

  if ('flowRate' in formData.spacingOrFlowRate) {
    return {flowRate: formData.spacingOrFlowRate.flowRate}
  }

  return {}
}

/**
 * Gets time-related parameters based on truck scenario
 */
const getTimeRelatedParams = (
  formData: FieldsForm
): Pick<OrderRequestPayload, 'loadSpace' | 'flowRate' | 'timeOnJobSite'> => ({
  ...getSpacingOrFlowRate(formData),
  ...getTimeOnJobSite(formData)
})

/**
 * Builds contact information section
 */
const buildContactInformation = (
  formData: FieldsForm
): OrderRequestPayload['contactInformation'] => {
  const orderPlacerContact = formData.orderPlacer || {}
  const siteContact = formData.siteContact || {}

  return {
    orderPlacerName: orderPlacerContact.name || '',
    orderPlacerNumber: phoneNumberToString(orderPlacerContact.phoneNumber),
    siteContactName: siteContact.name || '',
    siteContactNumber: phoneNumberToString(siteContact.phoneNumber)
  }
}

/**
 * Builds capacity information
 * For volume mode: sends the volume with quantityType 'truckCapacity'
 * For fullLoads mode: sends the fullLoads count with quantityType 'fullLoads'
 */
const buildCapacity = (formData: FieldsForm): OrderRequestPayload['capacity'] => {
  if (formData.quantity.type === 'fullLoads') {
    return {
      quantity: formData.quantity.fullLoads,
      quantityType: 'FullLoads',
      quantityChangeExpected: formData.plusLoadExpected || false
    }
  }
  return {
    quantity: formData.quantity.volume,
    quantityType: 'TruckCapacity',
    quantityChangeExpected: formData.plusLoadExpected || false
  }
}

/**
 * Gets retarder value if configured
 */
const getRetarderValue = (formData: FieldsForm): number | null => {
  if (formData.retarder && formData.retarder.retarder > 0) {
    return formData.retarder.retarder
  }
  return null
}

/**
 * Gets mix characteristics values (slump and set control)
 */
const getMixCharacteristics = (formData: FieldsForm): {slump: string; setControl: string} => {
  const slump = formData.materialVariations?.slump?.value || ''
  const setControl = formData.materialVariations?.setControl?.value || ''
  return {
    slump,
    setControl
  }
}

/**
 * Builds optional fields (notes, instructions, unloadingMethod)
 */
const buildOptionalFields = (formData: FieldsForm) => ({
  ...(formData.unloadingMethod
    ? {unloadingMethod: formData.unloadingMethod.unloadingMethodCode}
    : {}),
  ...(formData.additionalNotes ? {orderCreateNotes: formData.additionalNotes} : {}),
  ...(formData.driverInstructions ? {instructions: formData.driverInstructions} : {})
})

/**
 * Maps order intake form values to order request API payload.
 *
 * This is pure business logic that transforms form data into the structure
 * expected by the v2/orderrequests endpoint. The endpoint creates an async
 * order request that will be processed by the backend.
 *
 * @param formData - Order form values from react-hook-form
 * @param options - Additional context (site info, copiedFromOrderId)
 * @returns API payload ready to send to v2/orderrequests
 */
export const mapOrderFormToOrderRequest = (
  formData: FieldsForm,
  options: MapOrderFormToOrderRequestOptions
): OrderRequestPayload => {
  const {site, copiedFromOrderId = '', orderIndex = 0} = options
  const mixCharacteristics = getMixCharacteristics(formData)

  return {
    copiedFromOrderId,
    country: site.country,
    businessLine: 'RMC',
    orgUnitId: site.orgUnitId,
    customerId: site.customerId,
    siteId: site.siteId,
    contractNumber: formData.contract?.contractNumber ?? '',
    materialNumber: formData.material?.materialNumber ?? '',
    contractItem: formData.contract?.contractItemPositionNumber ?? '',
    capacity: buildCapacity(formData),
    ...(formData.maxLoadSize !== undefined ? {maxLoadSize: formData.maxLoadSize} : {}),
    truckType: formData.truckType,
    concreteUsage: formData.concreteUsage || '',
    contactInformation: buildContactInformation(formData),
    customerReference: formData.poNumber || '',
    shippingDate: formatShippingDate(formData, orderIndex),
    retarder: getRetarderValue(formData),
    isConfirmedByCustomer: formData.isConfirmedOrder || false,
    isTestBooked: formData.isTestBooked || false,
    testingRemarks: '',
    projectReference: '',
    purchaseOrderPin: formData.confirmationPin || '',
    slump: mixCharacteristics.slump,
    setControl: mixCharacteristics.setControl,
    ...getTimeRelatedParams(formData),
    ...buildOptionalFields(formData),
    ...buildPumpConfiguration(formData)
  }
}
